import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle } from 'lucide-react'
import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { useGeolocationStore } from '@/stores/geolocation-store'
import { useTimelineStore } from '@/stores/timeline-store'
import { cn } from '@/utils/cn'

/**
 * Surfaces a non-modal warning whenever the active timeline filter would
 * truncate one or more tracked entity trajectories AND the tracks layer is
 * NOT in `full` scope. One click on "Show full tracks" flips the layer to
 * `full` so the user keeps full context.
 */
export interface TracksLayerBadgeProps {
  className?: string
}

export function TracksLayerBadge({ className }: TracksLayerBadgeProps) {
  const tracksTemporalScope = useGeolocationStore(s => s.tracksTemporalScope)
  const setTracksTemporalScope = useGeolocationStore(s => s.setTracksTemporalScope)
  const tracks = useGeolocationStore(s => s.tracks)
  const filterStart = useTimelineStore(s => s.filterStart)
  const filterEnd = useTimelineStore(s => s.filterEnd)

  const truncatedCount = useMemo(() => {
    if (!filterStart || !filterEnd) return 0
    const fStart = Date.parse(filterStart)
    const fEnd = Date.parse(filterEnd)
    if (Number.isNaN(fStart) || Number.isNaN(fEnd)) return 0
    let count = 0
    for (const points of Object.values(tracks)) {
      if (!points.length) continue
      const tFirst = Date.parse(points[0].timestamp)
      const tLast = Date.parse(points[points.length - 1].timestamp)
      if (tFirst < fStart || tLast > fEnd) count++
    }
    return count
  }, [tracks, filterStart, filterEnd])

  const visible = tracksTemporalScope !== 'full' && truncatedCount > 0

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.96 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          role="status"
          className={cn(
            'pointer-events-auto absolute left-1/2 top-3 z-30 -translate-x-1/2',
            // Phone: drop below the full-width search bar, use the width next
            // to the tool column, and let the message wrap in a rounded box.
            'max-md:top-16 max-md:w-max max-md:max-w-[calc(100%-7.5rem)] max-md:rounded-2xl',
            'flex items-center gap-2 rounded-full border border-warning/40 bg-warning/15 px-3 py-1.5',
            'text-2xs text-warning shadow-[0_8px_24px_-12px_hsl(var(--foreground)/0.30)] backdrop-blur-xl',
            className
          )}
        >
          <AlertTriangle className="size-3.5 shrink-0" />
          <span className="text-foreground/90">
            {truncatedCount} tracked {truncatedCount === 1 ? 'entity spans' : 'entities span'}{' '}
            outside the current time filter
          </span>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setTracksTemporalScope('full')}
            className="h-6 px-2 text-3xs uppercase tracking-wide"
          >
            Show full tracks
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
